import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button, Dialog, Flex, ServerErrorCallout } from '@/components/common';
import { IntentionsPicker } from '@/components/intentions';
import { INTENTION_IDS, type IntentionId } from '@/constants/intentions';
import { useAuth } from '@/hooks/useAuth';
import { analyticsClient } from '@/lib';
import { userService } from '@/services';
import { useFilterStore } from '@/stores/filterStore';

interface SetIntentionsDialogProps {
  /** Current profile intentions — pass from parent so the picker opens pre-selected. */
  initialIntentions?: IntentionId[];
  /** Whether the user has marked themselves as taking a break this week. */
  takingABreak?: boolean;
}

function isIntentionId(value: string): value is IntentionId {
  return (INTENTION_IDS as readonly string[]).includes(value);
}

export function SetIntentionsDialog({
  initialIntentions = [],
  takingABreak = false,
}: SetIntentionsDialogProps) {
  const { t } = useTranslation('discover');
  const { user } = useAuth();
  const showIntentions = useFilterStore(s => s.showIntentions);
  const setShowIntentions = useFilterStore(s => s.setShowIntentions);

  const [selected, setSelected] = useState<IntentionId[]>([]);
  const [offThisWeek, setOffThisWeek] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const seeded = initialIntentions.filter(isIntentionId);
      setSelected(seeded);
      setOffThisWeek(takingABreak);
      setError(null);
      setLoading(false);
    }
    setShowIntentions(open);
  };

  const submit = async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError(null);
    const oldIntentions = initialIntentions;
    const newIntentions = offThisWeek ? [] : selected;

    try {
      await userService.updateProfile(user.uid, {
        intentions: newIntentions,
        takingABreak: offThisWeek,
      });
      analyticsClient.capture('intention_changed', {
        old_intentions: oldIntentions,
        new_intentions: newIntentions,
        taking_a_break: offThisWeek,
        intention_count: newIntentions.length,
      });
      setShowIntentions(false);
    } catch (err) {
      console.error(err);
      setError(t('iWant.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={showIntentions} onOpenChange={handleOpenChange} variant="fullscreen">
      <Dialog.Trigger>
        <Button
          type="button"
          variant="outline"
          color="gray"
          highContrast
          radius="full"
          size="sm"
          fullWidth={false}
        >
          {t('iWant.edit')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content aria-describedby={undefined}>
        <Dialog.Title backButton></Dialog.Title>
        <Flex direction="column" gap="2">
          {error ? <ServerErrorCallout message={error} /> : null}
          <IntentionsPicker
            selected={selected}
            offThisWeek={offThisWeek}
            onSelectedChange={ids => {
              setSelected(ids);
              if (ids.length > 0) setOffThisWeek(false);
            }}
            onOffThisWeekChange={setOffThisWeek}
            onSubmit={() => void submit()}
            loading={loading}
          />
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
